import { useCallback, useMemo } from 'react';

import { type GasPayload, type GasScreenEventPayload } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ALL_PRODUCTS_STRING, DEFAULT_GAS_CHANNEL } from '../../../common/constants/analytics';
import { sha256Hash } from '../../../common/utils/events/sha256-hash';
import {
	useAnalyticsAttributes,
	useApplicationMode,
	useExperimentConfig,
	useProductEdition,
	useSearchSessionId,
} from '../../store/bootstrap';
import {
	useFetchEntitiesRequestId,
	useFetchSpacesRequestId,
	useQuickFindQuery,
} from '../../store/quick-find';
import {
	useFilterAttributesForTracking,
	useQueryUpdatedTime,
	useQueryVersion,
	useSelectedProduct,
} from '../../store/search';

export const useQuickFindAnalytics: () => {
	fireAnalyticsEvent: (payload: GasPayload) => void;
	fireAnalyticsScreenEvent: (payload: GasScreenEventPayload) => void;
} = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent = useCallback(
		(payload: GasPayload): void => {
			createAnalyticsEvent({
				...payload,
				source: 'searchDialog',
			}).fire(DEFAULT_GAS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireAnalyticsScreenEvent = useCallback(
		(payload: GasScreenEventPayload): void => {
			createAnalyticsEvent({
				...payload,
				eventType: 'screen',
				source: 'searchDialog',
			}).fire(DEFAULT_GAS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	return { fireAnalyticsEvent, fireAnalyticsScreenEvent };
};

export const useQuickFindAttributes: () => {
	commonAttributes: {
		queryUpdatedAgo: number | undefined;
		queryVersion: number;
		searchSessionId: string;
		fetchEntitiesTraceId: string | undefined;
		fetchSpacesTraceId: string | undefined;
		isQuickFind: boolean;
		mode: import('../../../common/constants/application-modes').ApplicationModes;
		productSelected: string;
		filters: string[];
		filterSelections: import('../../../common/utils/events/types').FilterSelectionType[];
		numberOfTerms: number;
		queryHash: string;
		queryHashSHA256: string;
		queryLength: number;
		wordCount: number;
		productEdition: import('@atlassian/search-client').TProductEdition | undefined;
		frontendExperiments: {
			activeExperiments: string[];
		};
	};
	nonPrivacySafeAttributes: {
		query: string;
	};
} = () => {
	const [searchSessionId] = useSearchSessionId();
	const fetchEntitiesRequestId = useFetchEntitiesRequestId();
	const fetchSpacesRequestId = useFetchSpacesRequestId();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryUpdatedTime] = useQueryUpdatedTime();
	const quickFindQuery = useQuickFindQuery();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryVersion] = useQueryVersion();
	const [experimentConfig] = useExperimentConfig();
	const applicationMode = useApplicationMode();
	const [analyticsAttributes] = useAnalyticsAttributes();
	const [productSelected] = useSelectedProduct();
	const filters = useFilterAttributesForTracking();
	const [productEdition] = useProductEdition();

	return useMemo(() => {
		const { frontendExperiments = {} } = experimentConfig;
		const { filterSelections, filters: filterCategories } = filters;

		const commonAttributes = {
			productEdition,
			frontendExperiments: {
				activeExperiments: Object.keys(frontendExperiments).filter(
					(experiment) => frontendExperiments[experiment]?.serveExperiment,
				),
				...frontendExperiments,
			},
			...getCommonQueryAttributes(quickFindQuery),
			queryUpdatedAgo: queryUpdatedTime ? Date.now() - queryUpdatedTime : undefined,
			queryVersion,
			searchSessionId,
			fetchEntitiesTraceId: fetchEntitiesRequestId,
			fetchSpacesTraceId: fetchSpacesRequestId,
			isQuickFind: true,
			mode: applicationMode,
			productSelected: productSelected || ALL_PRODUCTS_STRING,
			filters: filterCategories,
			filterSelections,
			...analyticsAttributes,
		};

		const nonPrivacySafeAttributes = {
			query: quickFindQuery,
		};

		return { commonAttributes, nonPrivacySafeAttributes };
	}, [
		analyticsAttributes,
		applicationMode,
		experimentConfig,
		fetchEntitiesRequestId,
		fetchSpacesRequestId,
		filters,
		productEdition,
		productSelected,
		queryUpdatedTime,
		queryVersion,
		quickFindQuery,
		searchSessionId,
	]);
};

export const getWordCount = (query: string): number => (query.match(/\w+/g) || []).length;

export const getCommonQueryAttributes: (query: string) => {
	numberOfTerms: number;
	queryHash: string;
	queryHashSHA256: string;
	queryLength: number;
	wordCount: number;
} = (query: string) => {
	const wordCount = getWordCount(query);
	return {
		numberOfTerms: wordCount,
		queryHash: sha256Hash(query),
		queryHashSHA256: sha256Hash(query),
		queryLength: query.length,
		wordCount,
	};
};
